const URL_PATTERN = /url\(["']?(.*?)["']?\)/

// Never leave the leaderboard waiting on a flag that is not coming.
const SAFETY_TIMEOUT_MS = 4000

// The image for a state code, from the initial state's code -> asset URL map
// (keys upcased like the API's state_isos). Unknown codes have no flag.
export const stateFlagUrlFor = (code, stateFlagUrls) =>
  (code ? stateFlagUrls?.[String(code).toUpperCase()] : undefined) ?? null

// The distinct flag assets an event's players use.
export const collectFlagAssets = (scopes, displayedFlags, stateFlagUrlsByCode = {}) => {
  const countryCodes = new Set()
  const stateFlagUrls = new Set()

  Object.values(scopes ?? {}).forEach((aggregates) => {
    const list = aggregates ?? []

    list.forEach((agg) => {
      if (displayedFlags === 'country_flags') {
        const codes = agg?.country ?? []
        codes.forEach((entry) => {
          const code = entry?.alpha_2?.toLowerCase()
          if (code) {
            countryCodes.add(code)
          }
        })
      }

      if (displayedFlags === 'state_flags') {
        const codes = agg?.stateIsos ?? []
        codes.forEach((code) => {
          const url = stateFlagUrlFor(code, stateFlagUrlsByCode)
          if (url) {
            stateFlagUrls.add(url)
          }
        })
      }
    })
  })

  return { countryCodes: [ ...countryCodes ], stateFlagUrls: [ ...stateFlagUrls ] }
}

// A country flag's URL is known only to the stylesheet, as the background-image
// of .flag-icon-<code> - digested and CDN-hosted in production, where reading
// cssRules throws cross-origin. Computed styles resolve it regardless, so the
// codes are measured on throwaway elements rather than parsed out of the sheet.
const resolveCountryFlagUrls = (codes) => {
  if (!codes.length) {
    return []
  }

  const probe = document.createElement('div')
  probe.setAttribute('aria-hidden', 'true')
  probe.style.cssText = 'position:absolute;left:-9999px;top:0;width:0;height:0;overflow:hidden'

  codes.forEach((code) => {
    const span = document.createElement('span')
    span.className = `flag-icon flag-icon-${code}`
    probe.appendChild(span)
  })

  document.body.appendChild(probe)

  const urls = [ ...probe.children ]
    .map((element) => URL_PATTERN.exec(window.getComputedStyle(element).backgroundImage)?.[1])
    .filter(Boolean)

  probe.remove()

  return urls
}

// Loads every flag an event needs and resolves once they are all in cache, so
// the caller can hold the rows back until the flags would paint with them.
//
// Resolves on error as well as on load - a flag that will never arrive must not
// hold the leaderboard - and gives up after SAFETY_TIMEOUT_MS regardless.
export const preloadFlags = ({ countryCodes, stateFlagUrls }) => {
  const urls = [ ...resolveCountryFlagUrls(countryCodes), ...stateFlagUrls ]

  if (!urls.length) {
    return { promise: Promise.resolve(), cancel: () => {} }
  }

  let settled = false
  let timer = null

  const promise = new Promise((resolve) => {
    const finish = () => {
      if (!settled) {
        settled = true
        clearTimeout(timer)
        resolve()
      }
    }

    let remaining = urls.length
    const countDown = () => {
      remaining -= 1
      if (remaining <= 0) {
        finish()
      }
    }

    urls.forEach((url) => {
      const image = new Image()
      let counted = false
      const once = () => {
        if (!counted) {
          counted = true
          countDown()
        }
      }

      image.onload = once
      image.onerror = once
      image.src = url

      if (image.complete) {
        once()
      }
    })

    timer = setTimeout(finish, SAFETY_TIMEOUT_MS)
  })

  return { promise, cancel: () => clearTimeout(timer) }
}
